/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useReducer } from 'react';
import { IApplicationParentRef, IApplicationScopeRef, IBookmark, ReducerActionMap } from '../types';

/**
 *  TYPES DECLARATION
 */
export enum ApplicationTypes {
  SET_PARENT_REFS = 'SET_PARENT_REFS',
  SET_SCOPE_REFS = 'SET_SCOPE_REFS',
  SET_APPLICATIONS_MINE = 'SET_APPLICATIONS_MINE',
  SET_LOADING_APPLICATIONS_MINE = 'SET_LOADING_APPLICATIONS_MINE',
  SET_OFFICIAL_BOOKMARK = 'SET_OFFICIAL_BOOKMARK',
  SET_PERSONAL_BOOKMARK = 'SET_PERSONAL_BOOKMARK',
}

type ApplicationPayload = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [ApplicationTypes.SET_PARENT_REFS]: { parentRefs: IApplicationParentRef[] };
  [ApplicationTypes.SET_SCOPE_REFS]: { scopeRefs: IApplicationScopeRef[] };
  [ApplicationTypes.SET_APPLICATIONS_MINE]: {
    userApplicationsCount: number;
  };
  [ApplicationTypes.SET_LOADING_APPLICATIONS_MINE]: {
    loadingUserApplications: boolean;
  };
  [ApplicationTypes.SET_OFFICIAL_BOOKMARK]: {
    officialBookmarks: IBookmark[] | undefined;
  };
  [ApplicationTypes.SET_PERSONAL_BOOKMARK]: {
    personalBookmarks: IBookmark[] | undefined;
  };
};

type ApplicationActions = ReducerActionMap<ApplicationPayload>[keyof ReducerActionMap<ApplicationPayload>];

export type InitialStateType = {
  parentRefs: IApplicationParentRef[] | undefined;
  scopeRefs: IApplicationScopeRef[] | undefined;
  userApplicationsCount: number | undefined;
  loadingUserApplications: boolean;
  officialBookmarks: IBookmark[] | undefined;
  personalBookmarks: IBookmark[] | undefined;
};

/**
 *  VARIABLES DECLARATION
 */

const INITIAL_STATE: InitialStateType = {
  officialBookmarks: undefined,
  personalBookmarks: undefined,
  parentRefs: undefined,
  scopeRefs: undefined,
  userApplicationsCount: undefined,
  loadingUserApplications: true,
};

const ApplicationContext = React.createContext<
  | {
      state: InitialStateType;
    }
  | any
>({
  state: INITIAL_STATE,
});

ApplicationContext.displayName = 'ApplicationContext';

export const applicationReducer = (state: InitialStateType, action: ApplicationActions): InitialStateType => {
  switch (action.type) {
    case ApplicationTypes.SET_SCOPE_REFS: {
      const { scopeRefs } = action.payload;

      return {
        ...state,
        scopeRefs,
      };
    }
    case ApplicationTypes.SET_PARENT_REFS: {
      const { parentRefs } = action.payload;

      return {
        ...state,
        parentRefs,
      };
    }
    case ApplicationTypes.SET_APPLICATIONS_MINE: {
      const { userApplicationsCount } = action.payload;

      return {
        ...state,
        userApplicationsCount,
      };
    }
    case ApplicationTypes.SET_LOADING_APPLICATIONS_MINE: {
      const { loadingUserApplications } = action.payload;

      return {
        ...state,
        loadingUserApplications,
      };
    }
    case ApplicationTypes.SET_OFFICIAL_BOOKMARK: {
      const { officialBookmarks } = action.payload;

      return {
        ...state,
        officialBookmarks: officialBookmarks,
      };
    }
    case ApplicationTypes.SET_PERSONAL_BOOKMARK: {
      const { personalBookmarks } = action.payload;

      return {
        ...state,
        personalBookmarks: personalBookmarks,
      };
    }
    default: {
      return state;
    }
  }
};

type ApplicationProviderProps = {
  children: React.ReactNode;
  initialState?: InitialStateType;
};

const ApplicationProvider: React.FunctionComponent<ApplicationProviderProps> = ({
  children,
  initialState = INITIAL_STATE,
}: ApplicationProviderProps) => {
  const [state, dispatch] = useReducer(applicationReducer, initialState);

  const setParentRefs = React.useCallback(
    (parentRefs: IApplicationParentRef[]) =>
      dispatch({ type: ApplicationTypes.SET_PARENT_REFS, payload: { parentRefs } }),
    [dispatch]
  );

  const setScopeRefs = React.useCallback(
    (scopeRefs: IApplicationScopeRef[]) => dispatch({ type: ApplicationTypes.SET_SCOPE_REFS, payload: { scopeRefs } }),
    [dispatch]
  );

  const setLoadingUserApplications = React.useCallback(
    (loadingUserApplications: boolean) =>
      dispatch({
        type: ApplicationTypes.SET_LOADING_APPLICATIONS_MINE,
        payload: { loadingUserApplications },
      }),
    [dispatch]
  );

  const setApplicationsCountMine = React.useCallback(
    (userApplicationsCount: number) =>
      dispatch({
        type: ApplicationTypes.SET_APPLICATIONS_MINE,
        payload: { userApplicationsCount },
      }),
    [dispatch]
  );

  const setOfficialBookmark = React.useCallback(
    (officialBookmarks: IBookmark[] | undefined) =>
      dispatch({ type: ApplicationTypes.SET_OFFICIAL_BOOKMARK, payload: { officialBookmarks } }),
    [dispatch]
  );

  const setPersonalBookmark = React.useCallback(
    (personalBookmarks: IBookmark[] | undefined) =>
      dispatch({ type: ApplicationTypes.SET_PERSONAL_BOOKMARK, payload: { personalBookmarks } }),
    [dispatch]
  );

  const value = React.useMemo(
    () => ({
      ...state,
      setParentRefs,
      setScopeRefs,
      setLoadingUserApplications,
      setOfficialBookmark,
      setPersonalBookmark,
      setApplicationsCountMine,
    }),
    [
      state,
      setParentRefs,
      setScopeRefs,
      setLoadingUserApplications,
      setOfficialBookmark,
      setPersonalBookmark,
      setApplicationsCountMine,
    ]
  );

  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
};

export const useApplicationState = (): {
  setParentRefs: (parentRefs: IApplicationParentRef[]) => void;
  setScopeRefs: (scopeRefs: IApplicationScopeRef[]) => void;
  setOfficialBookmark: (officialBookmarks: IBookmark[] | undefined) => void;
  setPersonalBookmark: (personalBookmarks: IBookmark[] | undefined) => void;
  setApplicationsCountMine: (userApplicationsCount: number) => void;
  setLoadingUserApplications: (loadingUserApplications: boolean) => void;
} & InitialStateType => {
  const context = React.useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error(`useApplication must be used within an ApplicationProvider`);
  }
  return context;
};

export { ApplicationContext, ApplicationProvider };
